import dayjs from 'dayjs';

import { WEEKDAYS } from '../../../../constants/meetings';
import { AvailabilityObject, IntervalsObject } from '../../../../interfaces/meeting';

const timeToMinutes = (timeString: string): number | null => {
  if (!timeString) return null;
  const timeParts  = timeString.split(':').map(Number);
  if (timeParts.length !== 3 || timeParts.some(isNaN)) {
    return null; 
  }
  const [hours, minutes, seconds] = timeParts;

  // Calculate total minutes
  const totalMinutes = hours * 60 + minutes + seconds / 60;

  return totalMinutes;
};

const formatDate = (dateString: string): string | null => {
  const date = new Date(dateString);
  
  if (isNaN(date.getTime())) {
    return null; 
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const convertTimeTo12HourFormat = (time: string): string => {
  const [hoursString, minutesString, period] = time.split(/:| /);
  const hours = parseInt(hoursString, 10);
  const minutes = parseInt(minutesString, 10);

  if (isNaN(hours) || isNaN(minutes) || (period !== 'AM' && period !== 'PM')) {
    return ''; 
  }

  let formattedHours = hours;
  if (hours === 0) {
    formattedHours = 12; 
  } else if (hours > 12) {
    formattedHours -= 12;
  }

  return `${formattedHours.toString().padStart(2, '0')}:${minutesString.padStart(2, '0')} ${period}`;
};

const convertTimeTo24HourFormat = (time: string): string => {
  const [timeString, period] = time.split(' ');
  const [hours, minutes] = timeString.split(':').map(Number);

  let hours24 = hours;
  if (period === 'PM' && hours !== 12) {
    hours24 += 12;
  } else if (period === 'AM' && hours === 12) {
    hours24 = 0;
  }

  const formattedTime = `${hours24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  return formattedTime;
};

const convertTimeRangeHourFormat = (timeRangeArray: IntervalsObject[], isto12Hour?: boolean): IntervalsObject[] => {
  if (!Array.isArray(timeRangeArray)) return [];
  if (isto12Hour) {
    return timeRangeArray.map((time) => (
      !time.is_delete ? {
        from: convertTimeTo12HourFormat(time.from),
        to: convertTimeTo12HourFormat(time.to),
        id: time.id,
        is_delete: time.is_delete,
      } : time
    ));
  }
  return timeRangeArray.map((time) => (
    !time.is_delete ? {
      from: convertTimeTo24HourFormat(time.from),
      to: convertTimeTo24HourFormat(time.to),
      id: time.id,
      is_delete: time.is_delete,
    } : time));
};

const checkIfInIntervals = (timeValue: string, intervals: IntervalsObject[]): string => {
  const isExisting = intervals.some(interval => interval.from === timeValue);
  return isExisting ? 'none' : '';
};

const generateTimeArray = (durationInMinutes: number | null) => {
  
  const timeArray = [];
  if (!durationInMinutes) return [];
  const startTime = new Date('2024-01-01T00:00:00');

  while (startTime.getDate() === 1) {
    const formattedTime = startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    timeArray.push(formattedTime);
    
    startTime.setMinutes(startTime.getMinutes() + durationInMinutes);
  }
  return timeArray;
};

const isSameDate = (dateString1: string, dateString2: string): boolean => {
  return new Date(dateString1).toDateString() === new Date(dateString2).toDateString();
};

const isSameDateWithFormat =  (date1: Date | null, date2: Date | null) => {
  if (dayjs(date1).format('YYYYMMDD') === dayjs(date2).format('YYYYMMDD'))
    return true;
  
  return false;
};

const getEventForCurrentDay = (eventList: AvailabilityObject[], date: string): AvailabilityObject | undefined  => {
  const specificDay = eventList?.find((item: AvailabilityObject) => isSameDate(item?.weekday, date));
  if (specificDay)
    return specificDay;
  const weekDay = eventList?.find((item: AvailabilityObject) => item.weekday == new Date(date)?.getDay()?.toString());
  if (weekDay)
    return weekDay;
  return undefined;
};

const getDatesInRange = (startDateVal: Date | null, endDateVal: Date | null) => {
  if (!startDateVal || !endDateVal) return [];

  const dates = [];
  const currentDate = new Date(startDateVal);

  while (currentDate <= endDateVal) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

const getValueToPropertyName = (value: number): string => {
  const entry = Object.entries(WEEKDAYS).find(([, v]) => v === value);
  if (entry) {
    return entry[0].charAt(0).toUpperCase() + entry[0].slice(1).toLowerCase();
  }
  return '';
};

function isDateInThePast(date: string): boolean {
  const currentDate = new Date();
  const dateSelected = new Date(date);
  if (isNaN(dateSelected.getTime()))
    return false;
  return dateSelected < currentDate;
}

export { timeToMinutes, formatDate, convertTimeRangeHourFormat, checkIfInIntervals, generateTimeArray, isSameDate, isSameDateWithFormat, getEventForCurrentDay, getDatesInRange, getValueToPropertyName, convertTimeTo12HourFormat, isDateInThePast };